'use client';

import { TypographyH3, TypographyP2 } from '@heidi/design-system/typography';
import { useTranslations } from '@heidi/runtime/i18n';
import { AudioLines, ChevronDown, File, Mic, PencilLine, PhoneCall } from 'lucide-react';
import { m, useReducedMotion } from 'motion/react';

import { SkeletonBlock } from './PreviewPanelContent';
import {
  PREVIEW_PANEL_VISUAL_EASE_OUT_CUBIC,
  PREVIEW_PANEL_VISUAL_STAGGER_DELAY_S,
} from './PreviewPanelVisualTransition';

function MicPreviewDropdownMenu({ animate }: { animate: boolean }) {
  const { t } = useTranslations();
  const prefersReducedMotion = useReducedMotion();
  const shouldAnimate = animate && !prefersReducedMotion;

  const Wrapper = shouldAnimate ? m.div : 'div';
  const wrapperProps = shouldAnimate
    ? {
        initial: { opacity: 0, y: -8 },
        animate: { opacity: 1, y: 0 },
        transition: {
          duration: 0.3,
          ease: PREVIEW_PANEL_VISUAL_EASE_OUT_CUBIC,
          delay: PREVIEW_PANEL_VISUAL_STAGGER_DELAY_S + 0.1,
        },
      }
    : {};

  return (
    <Wrapper
      className="absolute right-6 top-6 z-10 flex flex-col items-end gap-2"
      {...wrapperProps}
    >
      <div className="flex items-center gap-1.5 rounded-lg bg-validation-success px-3 py-2 text-sm font-medium text-white">
        {t('signup.micPreviewTranscribe')}
        <ChevronDown size={16} />
      </div>
      <div className="w-[204px] rounded-lg border border-border bg-white p-1 shadow-md">
        <DropdownItem
          icon={<AudioLines size={16} />}
          label={t('signup.micPreviewTranscribe')}
          active
        />
        <DropdownItem icon={<Mic size={16} />} label={t('signup.micPreviewDictate')} />
        <DropdownItem
          icon={<PhoneCall size={16} />}
          label={t('signup.micPreviewTelehealth')}
        />
      </div>
    </Wrapper>
  );
}

export function MicPermissionsPreview() {
  const { t } = useTranslations();

  return (
    <div className="absolute inset-0 flex items-center py-10 pl-10">
      <div className="relative h-4/5 w-full">
        <div className="absolute inset-y-0 left-0 flex min-w-[110%] flex-col gap-4 overflow-hidden rounded-2xl bg-surface-accent py-6 pl-6 shadow-lg">
          <div className="flex flex-col gap-1">
            <TypographyH3 className="text-text-secondary">
              {t('signup.micPreviewPatientName')}
            </TypographyH3>
            <TypographyP2 className="text-text-tertiary">
              {t('signup.micPreviewTimestamp')}
            </TypographyP2>
          </div>

          <div className="flex items-center gap-2">
            <div className="flex h-9 items-center gap-1.5 rounded-lg bg-white px-3 py-2.5">
              <AudioLines size={20} className="text-accent-pink" />
              <span className="text-sm font-medium leading-none text-text-primary">
                {t('signup.previewTranscript')}
              </span>
            </div>
            <div className="flex h-9 items-center gap-1.5 rounded-lg px-3 py-2.5">
              <PencilLine size={20} className="text-text-secondary" />
              <span className="text-sm font-medium leading-none text-text-secondary">
                {t('signup.previewNote')}
              </span>
            </div>
            <div className="flex h-9 items-center gap-1.5 rounded-lg px-3 py-2.5">
              <File size={20} className="text-text-secondary" />
              <span className="text-sm font-medium leading-none text-text-secondary">
                {t('signup.micPreviewReferralLetter')}
              </span>
            </div>
          </div>

          <div className="flex flex-1 flex-col gap-6 rounded-2xl border-2 border-border bg-white p-6">
            <div className="flex max-w-[540px] flex-col gap-4">
              <SkeletonBlock width="w-[105px]" variant="muted" />
              <SkeletonBlock width="w-[353px]" variant="muted" />
              <SkeletonBlock width="w-full" variant="muted" />
              <SkeletonBlock width="w-[433px]" variant="muted" />
            </div>
            <div className="flex max-w-[540px] flex-col gap-4">
              <SkeletonBlock width="w-[353px]" variant="muted" />
              <SkeletonBlock width="w-full" variant="muted" />
              <SkeletonBlock width="w-[433px]" variant="muted" />
            </div>
            <div className="flex max-w-[540px] flex-col gap-4">
              <SkeletonBlock width="w-[353px]" variant="muted" />
              <SkeletonBlock width="w-full" variant="muted" />
              <SkeletonBlock width="w-[433px]" variant="muted" />
            </div>
          </div>
        </div>

        <MicPreviewDropdownMenu animate />
      </div>
    </div>
  );
}

function DropdownItem({
  icon,
  label,
  active = false,
}: {
  icon: React.ReactNode;
  label: string;
  active?: boolean;
}) {
  return (
    <div
      className={`flex items-center gap-2 rounded-xl px-2 py-1.5 text-sm text-text-primary ${active ? 'bg-surface-hover' : ''}`}
    >
      {icon}
      {label}
    </div>
  );
}
